/**
 * FLAT SOFFIT + CORBELS round (2026-10-07, Matt): the FLAT eave soffit — "the soffit
 * returns flat back from the fascia on the underside of the eave framing", the rafter
 * tails under it becoming CORBELS that "sit under the flat soffit and don't need to
 * extend back into the building framing". The common hip-roof eave detail; on a gable
 * the flat soffit RETURNS a short way along each rake (the cornice return) "to help
 * clean up the transition" from eave to rake.
 *
 * This module is the ONE place the flat soffit's ELEVATION and its LEVEL PLAN REGIONS
 * derive — read by the mass (underside.ts → generate3d + walls-attach) and by the
 * corbels (framing.ts) alike, so a corbel's top and the soffit it hangs under can never
 * disagree. It imports from neither of those files (framing ↔ underside would otherwise
 * cycle through it); the E1 eave-crop read lives here for the same reason.
 *
 * THE SOFFIT ELEVATION: the eave's sloped underside AT THE FASCIA LINE, raised by the
 * E1 eave crop (the eave structure depth is the fascia depth now), held level all the
 * way back to the wall. An eave cropped to a 2×6 reads as a 2×6-deep fascia over a
 * level soffit. The soffit sits BELOW the bearing plane wherever the overhang's drop
 * exceeds the crop (the usual case), so the step at the wall line goes DOWN from the
 * plate line where E1's went up — generate3d's step face handles both hands.
 *
 * THE LEVEL REGION of a flat face's eave zone: the band outside its bearing edge (the
 * whole eave band, running through hip and valley corners — the soffit is continuous
 * around a hip roof) plus, at each end where the neighboring boundary edge is a GABLE
 * with a rake overhang, the RETURN: the rake band from the fascia line up the gable
 * for `soffitReturnFt`. Past the return the rake soffit resumes sloped and the mass
 * closes the difference with a plumb face (the return's end — the classic "pork chop").
 * A return of 0 = none: the level soffit stops at the gable line.
 */
import type { Pt2, ResolvedElement } from '@aj/bim-kernel'
import { FASCIA_SIZES, lumberSize } from '@aj/bim-kernel'
import { signedArea } from '../../clipper'
import type { ResolvedRoofSolve, RoofFace } from './solve'

/** the schema default for `soffitReturnFt` (a blank/null instance value means this) */
export const DEFAULT_SOFFIT_RETURN_FT = 1.5

/**
 * E1: the eave crop in FEET for a face's resolved eave structure depth (inches;
 * null = no crop). Depths are measured perpendicular to the slope, like the assembly
 * layers themselves — callers convert plumb with the same √(1+m²) the build-up uses.
 * The crop is what comes OFF the assembly's structural core (`coreBoundary`), clamped
 * so a value ≥ the core depth means no crop and at least ¼″ of eave section always
 * remains (a degenerate 0″ entry can't zero the eave out). (Moved here from
 * underside.ts in the FLAT SOFFIT round; underside.ts re-exports it.)
 */
export function eaveCropFtFor(el: ResolvedElement, eaveDepthIn: number | null | undefined): number {
  if (eaveDepthIn == null || !Number.isFinite(eaveDepthIn) || eaveDepthIn < 0) return 0
  const a = el.assembly
  if (!a || !a.layers.length) return 0
  const nL = a.layers.length
  const s = Math.min(Math.max(a.coreBoundary?.start ?? 0, 0), nL - 1)
  const e = Math.min(Math.max(a.coreBoundary?.end ?? nL - 1, s), nL - 1)
  let core = 0
  for (let i = s; i <= e; i++) core += a.layers[i].thicknessFt || 0
  if (core <= 0) return 0
  return Math.max(0, Math.min(core - eaveDepthIn / 12, el.assemblyThicknessFt - 1 / 48))
}

/** Does this face carry a FLAT soffit that can actually exist (an eave with depth on a
 *  sloped, non-flying face)? */
export function roofFaceIsFlatSoffit(solved: ResolvedRoofSolve, f: RoofFace): boolean {
  return !!f.flatSoffit && !f.flying && !solved.flat && (f.overhangFt ?? 0) > 1e-6
}

/** THE FASCIA RULE (Matt, 2026-10-07): the soffit's bottom finish sits this far above
 *  the bottom of the fascia board, "so it never pops below the fascia" */
export const SOFFIT_ABOVE_FASCIA_FT = 0.5 / 12

/**
 * The flat soffit's absolute elevation for a face — null when the face has none.
 * With a FASCIA on the edge the fascia governs: ½″ above the board's bottom (the board
 * hangs from the roof-edge top line by its depth — trim.ts's own fascia geometry), so a
 * deeper roof build-up or a shallower fascia can never leave the soffit hanging below the
 * fascia; the eave structure above it is whatever fits (the eave rafters are cut to clear
 * the soffit framing — framing.ts). Without a fascia: the sloped underside at the fascia
 * line, raised by the E1 crop, held level back to the wall. (A joined roof's fascia size
 * is read off this roof's own params here, not the join authority — the one place the
 * two could disagree; the corbels read the authority.)
 */
export function flatSoffitZ(el: ResolvedElement, solved: ResolvedRoofSolve, f: RoofFace): number | null {
  if (!roofFaceIsFlatSoffit(solved, f)) return null
  const ov = f.overhangFt ?? 0
  const sq = Math.sqrt(1 + f.m * f.m)
  const zEave = f.zAt(f.origin) - f.m * ov           // the bearing plane at the fascia line
  const spec = solved.specs[f.edgeIndex]
  if (spec?.fascia) {
    const fascia = lumberSize(el.params.fasciaSize, FASCIA_SIZES) ?? FASCIA_SIZES[3]
    return zEave + el.assemblyThicknessFt * sq - fascia.depthIn / 12 + SOFFIT_ABOVE_FASCIA_FT
  }
  return zEave + eaveCropFtFor(el, f.eaveDepthIn) * sq
}

/** The roof's soffit return length at its rakes, feet (blank/null = the default). */
export function soffitReturnFtOf(el: ResolvedElement): number {
  const raw = el.params.soffitReturnFt
  if (raw == null || raw === '') return DEFAULT_SOFFIT_RETURN_FT
  const n = Number(raw)
  return Number.isFinite(n) ? Math.max(0, n) : DEFAULT_SOFFIT_RETURN_FT
}

/**
 * The plan polygons (CCW) that are LEVEL on a flat face — the caller intersects them
 * with the face's eave zone. Empty when the face has no flat soffit. Overlaps between
 * the polygons are fine (NonZero clipping reads them as one region).
 */
export function flatSoffitLevelPolys(el: ResolvedElement, solved: ResolvedRoofSolve, f: RoofFace): Pt2[][] {
  if (!roofFaceIsFlatSoffit(solved, f)) return []
  const ov = f.overhangFt ?? 0
  const d: Pt2 = [-f.uphill[0], -f.uphill[1]]      // downhill
  const e: Pt2 = [-d[1], d[0]]                       // along the bearing edge
  const dot = (p: Pt2, v: Pt2) => p[0] * v[0] + p[1] * v[1]
  const BIG = 1e4
  const ccw = (ring: Pt2[]): Pt2[] => (signedArea(ring) < 0 ? [...ring].reverse() : ring)
  const dO = dot(f.origin, d)
  const polys: Pt2[][] = []

  // the eave band along this face's bearing edge: through hip/valley corners the
  // level soffit runs on into the corner wedge (the face ring bounds it); at a gable
  // corner it stops at the corner and the RETURN below takes over
  const spec = solved.specs[f.edgeIndex]
  let eLo = -BIG, eHi = BIG
  const R = soffitReturnFtOf(el)
  if (spec) {
    const near = (p: Pt2, q: Pt2) => Math.hypot(p[0] - q[0], p[1] - q[1]) < 1e-6
    for (const [P, far] of [[spec.a, spec.b], [spec.b, spec.a]] as [Pt2, Pt2][]) {
      const nb = solved.specs.find((s) => s !== spec && (near(s.a, P) || near(s.b, P)))
      if (!nb || !nb.gable || !((nb.overhangFt ?? 0) > 1e-6)) continue
      const pe = dot(P, e)
      if (pe < dot(far, e)) eLo = pe; else eHi = pe
      if (!(R > 1e-6)) continue
      // the return: from the fascia line up the gable line for R, across the rake band
      const Q: Pt2 = near(nb.a, P) ? nb.b : nb.a
      const gl = Math.hypot(Q[0] - P[0], Q[1] - P[1])
      if (gl < 1e-6) continue
      const g: Pt2 = [(Q[0] - P[0]) / gl, (Q[1] - P[1]) / gl]
      const gd = dot(g, d)
      if (gd > -0.1) continue                      // the gable runs level/downhill — not a rake corner
      const perp: Pt2 = [-g[1], g[0]]
      const outward: Pt2 = dot([far[0] - P[0], far[1] - P[1]], perp) < 0 ? perp : [-perp[0], -perp[1]]
      const sE = ov / gd                           // along g to the fascia line (negative: downhill)
      const w1 = (nb.overhangFt ?? 0) + 0.5        // past the rake line (the eave ring clips it)
      const at = (s: number, w: number): Pt2 => [P[0] + g[0] * s + outward[0] * w, P[1] + g[1] * s + outward[1] * w]
      polys.push(ccw([at(sE - 0.5, -1), at(sE - 0.5, w1), at(sE + R, w1), at(sE + R, -1)]))
    }
  }
  const at = (pe: number, pd: number): Pt2 => [e[0] * pe + d[0] * pd, e[1] * pe + d[1] * pd]
  polys.push(ccw([at(eLo, dO - 1), at(eHi, dO - 1), at(eHi, dO + ov + 1), at(eLo, dO + ov + 1)]))
  return polys
}
